"use client";
import * as React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import logger from "@/lib/logger";
import styles from "./index.module.css";
import { uppercaseBreadcrumbAbbreviations } from "@/utils/functions/formatting";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import decodeMalformedText from "@/utils/decodeMalformedText";
import useWebview from "@/webview/useWebview";
import { getLocaleAndCanonicalPath } from "@/lib/path/localePath";
import useTranslations, { type Messages } from "@/i18n/useTranslations";

const TRANSLATABLE_SEGMENTS = [
  "resources",
  "become",
] as const satisfies ReadonlyArray<keyof Messages["Breadcrumbs"]>;

type TranslatableSegment = (typeof TRANSLATABLE_SEGMENTS)[number];

export interface BreadcrumbsProps {
  variant?: string;
  path: string;
  pageName?: string;
  linksToShowOnSmallScreen?: number;
  title?: string;
  isExecEd?: boolean;
  onlyShowLastLink?: boolean;
  containerClassName?: string;
}

export default function Breadcrumbs({
  variant = "light",
  path,
  pageName,
  linksToShowOnSmallScreen,
  title,
  isExecEd = false,
  onlyShowLastLink = false,
  containerClassName = "",
}: BreadcrumbsProps) {
  const { getUrl } = useWebview();
  const t = useTranslations("Breadcrumbs");
  if (typeof path !== "string") {
    logger.error("Invalid prop: path should be a string");
    return null;
  }
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const { locale, canonicalPath } = getLocaleAndCanonicalPath(normalizedPath);
  const hrefPrefix = locale !== "en" ? `/${locale}` : "";

  const slugItems = canonicalPath.split("/").filter(Boolean);
  let pathItems = slugItems.map((item) => item.replace(/-/g, " "));
  pathItems = uppercaseBreadcrumbAbbreviations(pathItems);
  pathItems = pathItems.map((item, i) => {
    const slug = slugItems[i];
    return (TRANSLATABLE_SEGMENTS as ReadonlyArray<string>).includes(slug)
      ? t(slug as TranslatableSegment)
      : item;
  });
  if (pageName) {
    pathItems[pathItems.length - 1] = decodeMalformedText(pageName);
  }

  const isLightVariant = variant === "light";

  const totalLinks = pathItems.length;

  const hideHomeLink = isExecEd
    ? linksToShowOnSmallScreen && totalLinks - 1 === linksToShowOnSmallScreen
    : linksToShowOnSmallScreen && totalLinks - 1 > linksToShowOnSmallScreen;

  if (isExecEd && title) {
    pathItems[pathItems.length - 1] = title;
  }

  if (onlyShowLastLink) {
    const index =
      pathItems.length >= 2 ? pathItems.length - 2 : pathItems.length - 1;
    const component = pathItems[index];
    return (
      <React.Fragment>
        <BreadcrumbItem
          className={`
              ${
                isLightVariant
                  ? `${styles.breadcrumbs} text-primary-foreground capitalize`
                  : `${styles.breadcrumbs_dark} capitalize`
              }
              `}
          aria-current={false}
        >
          <BreadcrumbLink
            className={
              isLightVariant
                ? styles.breadcrumb_link
                : styles.breadcrumb_link_dark
            }
            href={`${hrefPrefix}/${slugItems.slice(0, index + 1).join("/")}`}
          >
            {component}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </React.Fragment>
    );
  }

  const defaultContainerClassName = "not-prose mb-4";

  return (
    <Breadcrumb
      className={
        containerClassName ? containerClassName : defaultContainerClassName
      }
    >
      <BreadcrumbList
        className={isExecEd ? "flex-nowrap sm:flex-wrap text-sm" : ""}
      >
        <BreadcrumbItem
          className={`
            ${hideHomeLink ? "sm:inline-flex hidden" : ""}
            ${
              isLightVariant
                ? `${styles.breadcrumbs} text-primary-foreground capitalize`
                : `${styles.breadcrumbs_dark} capitalize`
            }
            `}
        >
          <BreadcrumbLink
            className={
              isLightVariant
                ? styles.breadcrumb_link
                : styles.breadcrumb_link_dark
            }
            href={hrefPrefix || "/"}
          >
            {t("home")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        {pathItems.map((component, index) => {
          const isLastItem = index === pathItems.length - 1;
          // If linksToShowOnSmallScreen is passed, only show the specified number of links starting from the bottom of the list
          const isLinkVisibleOnMobile = isExecEd
            ? isLastItem
            : linksToShowOnSmallScreen
              ? index >= totalLinks - linksToShowOnSmallScreen - 1 &&
                index < totalLinks - 1
              : true;
          return (
            <React.Fragment key={index}>
              {linksToShowOnSmallScreen ? (
                <BreadcrumbSeparator
                  variant={variant}
                  className={!isLinkVisibleOnMobile ? "hidden sm:inline" : ""}
                >
                  <ChevronRightIcon
                    className="hidden sm:block text-white font-bold w-4 h-4 "
                    style={{
                      strokeWidth: 1,
                      stroke: variant === "light" ? "white" : "#00262b",
                    }}
                  />
                  <ChevronLeftIcon
                    className="block sm:hidden text-white font-bold w-4 h-4 "
                    style={{
                      strokeWidth: 1,
                      stroke: variant === "light" ? "white" : "#00262b",
                    }}
                  />
                </BreadcrumbSeparator>
              ) : (
                <BreadcrumbSeparator variant={variant} />
              )}

              <BreadcrumbItem
                className={`
                  ${!isLinkVisibleOnMobile ? "sm:inline-flex hidden" : ""}
                  ${
                    isLightVariant
                      ? `${styles.breadcrumbs} text-primary-foreground capitalize`
                      : `${styles.breadcrumbs_dark} capitalize`
                  }
                  `}
                aria-current={pathItems.length === index + 1 ? "page" : false}
              >
                {index === pathItems.length - 1 ? (
                  <span className={!isLightVariant ? "opacity-70" : ""}>
                    {component}
                  </span>
                ) : (
                  <BreadcrumbLink
                    className={
                      isLightVariant
                        ? styles.breadcrumb_link
                        : styles.breadcrumb_link_dark
                    }
                    href={getUrl(
                      `${hrefPrefix}/${slugItems.slice(0, index + 1).join("/")}`,
                    )}
                  >
                    {component}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
